import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneModal } from '@factorialco/f0-react/dist/experimental'
import { Form } from 'factorial-form'
import { InboxTaskListItemCompletionFragmentDoc } from 'generated/resources/graphql'

import TextArea from 'modules/core/components/Inputs/TextArea'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import { encodeActivity } from 'modules/core/lib/activities/utils'
import i18n from 'modules/core/lib/i18n'

import useBulkApproveRejectFlows from 'modules/approvals/hooks/useBulkApproveRejectFlows'

type Data = {
  materializedFlowId: string
  taskId: string
}

export const RejectApprovalFlowActivity: ActivityComponent<Data> = ({
  taskId,
  materializedFlowId,
}) => {
  const apolloClient = useApolloClient()

  const { open, closeActivity } = useActivity()
  const navigate = useNavigate()
  const { search } = useLocation()

  const { access } = useCurrentSession()

  const currentAccessId = access.id

  const { reject, disabled } = useBulkApproveRejectFlows()

  const handleCancel = useCallback(() => {
    closeActivity()
    navigate({
      search,
      hash: encodeActivity('notifications.task', {
        id: taskId,
      }),
    })
  }, [closeActivity, navigate, search, taskId])

  const handleAfterReject = useCallback(() => {
    apolloClient.writeFragment({
      id: `TasksInboxTask:${taskId}`,
      fragment: InboxTaskListItemCompletionFragmentDoc,
      fragmentName: 'InboxTaskListItemCompletion',
      data: {
        __typename: 'TasksInboxTask',
        id: Number(taskId),
        completedAt: new Date().toISOString(),
        status: 'done',
      },
    })
    handleCancel()
  }, [taskId, handleCancel, apolloClient])

  const form = useLazyRef(
    () =>
      new Form(
        {
          reason: '',
        },
        {
          reason: 'string',
        }
      )
  ).current

  const handleSubmit = useCallback(() => {
    const reason = form.get('reason').value

    const actionData = {
      accessId: currentAccessId,
      materializedFlowId: Number(materializedFlowId),
    }

    return reason
      ? reject({ ...actionData, reason }, handleAfterReject)
      : reject(actionData, handleAfterReject)
  }, [currentAccessId, form, materializedFlowId, reject, handleAfterReject])

  return (
    <>
      <OneModal isOpen={open} onClose={closeActivity} position="center">
        <OneModal.Header
          title={i18n.t('settings.approvals.approval_request_view.reject_leave_modal.title')}
        />
        <OneModal.Content withPadding>
          <TextArea
            placeholder={i18n.t('dashboard.todo.approvals.general.reject.label')}
            field={form.get('reason')}
            minRows={5}
          />
        </OneModal.Content>
        <OneModal.Footer>
          <Box flexGrow flexDirection="row" gap="s12" justifyContent="flexEnd">
            <F0Button
              label={i18n.t('common.cancel')}
              variant="neutral"
              disabled={disabled}
              onClick={handleCancel}
            />
            <F0Button
              label={i18n.t('dashboard.todo.approvals.general.reject.button')}
              disabled={disabled}
              onClick={handleSubmit}
            />
          </Box>
        </OneModal.Footer>
      </OneModal>
    </>
  )
}
